import { FC } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography, Svg } from '@altermaind/web/ui';
import { Claims } from '@/_shared/constants';
import { ClaimsComponent } from '../Claims/Claims';

type Props = {
  onRefreshData?: () => void;
  onViewDetail?: () => void;
  onEdit?: () => void;
  onEditClaimKey?: Claims;
  onDelete?: () => void;
  onDeleteClaimKey?: Claims;
  onClose?: () => void;
  onCustomLabel?: string;
  onCloseClaimKey?: Claims;
  onCustom?: () => void;
  onCustomClaimKey?: Claims;
  onAdd?: () => void;
  onFilter?: () => void;
  onAddClaimKey?: Claims;
  hasElement?: boolean;
  'prefix-testid'?: string;
};

export const CtaContainer: FC<Props> = ({
  onRefreshData,
  onViewDetail,
  onEdit,
  onEditClaimKey,
  onDelete,
  onDeleteClaimKey,
  onClose,
  onCloseClaimKey,
  onCustom,
  onCustomLabel,
  onCustomClaimKey,
  onAdd,
  onFilter,
  onAddClaimKey,
  hasElement,
  'prefix-testid': prefixTestId,
}) => {
  const t = useTranslations('ctaContainer');
  return (
    <Box display='flex' alignItems='flex-end' flexDirection='column'>
      <Box
        display='flex'
        alignItems='flex-end'
        flexDirection='column'
        justifyContent='space-between'
      >
        <Box display='flex' alignItems='center'>
          {onEdit && (
            <ClaimsComponent claimKey={onEditClaimKey}>
              <Box
                disabled={!hasElement}
                opacity={!hasElement ? 0.6 : 1}
                onClick={hasElement ? onEdit : undefined}
                mb='m'
                ml='s'
                display='flex'
                alignItems='center'
              >
                <Typography
                  data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-modify`}
                  variant='p2/light'
                  cursor='pointer'
                >
                  {t('edit')}
                </Typography>
              </Box>
            </ClaimsComponent>
          )}
          {onDelete && (
            <ClaimsComponent claimKey={onDeleteClaimKey}>
              <Box
                onClick={hasElement ? onDelete : undefined}
                disabled={!hasElement}
                opacity={!hasElement ? 0.6 : 1}
                mb='m'
                ml='s'
                display='flex'
                alignItems='center'
              >
                <Typography variant='p2/light' cursor='pointer'>
                  {t('remove')}
                </Typography>
              </Box>
            </ClaimsComponent>
          )}
          {onClose && (
            <ClaimsComponent claimKey={onCloseClaimKey}>
              <Box
                onClick={hasElement ? onClose : undefined}
                disabled={!hasElement}
                opacity={!hasElement ? 0.6 : 1}
                mb='m'
                ml='s'
                display='flex'
                alignItems='center'
              >
                <Typography
                  data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-close`}
                  variant='p2/light'
                  cursor='pointer'
                >
                  {t('close')}
                </Typography>
              </Box>
            </ClaimsComponent>
          )}
          {onAdd && (
            <ClaimsComponent claimKey={onAddClaimKey}>
              <Button
                data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-add`}
                mb='m'
                ml='s'
                variant='primary'
                px='xs'
                onClick={onAdd}
                type='button'
              >
                <Typography variant='p2' fontSize='xxxl' color='white'>
                  +
                </Typography>
                <Typography variant='p2' ml='xs' color='white'>
                  {t('new')}
                </Typography>
              </Button>
            </ClaimsComponent>
          )}
        </Box>
      </Box>
      <Box display='flex'>
        {onCustom && (
          <ClaimsComponent claimKey={onCustomClaimKey}>
            <Button
              data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-custom`}
              mr='s'
              variant='link/primary'
              onClick={onCustom}
              disabled={!hasElement}
              type='button'
            >
              <Svg name='info' fill='primary/text' size='xs' />
              <Typography as='span' variant='p2/light' ml='xxs'>
                {onCustomLabel}
              </Typography>
            </Button>
          </ClaimsComponent>
        )}
        {onViewDetail && (
          <Button
            data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-view-detail`}
            mr='s'
            variant='link/primary'
            onClick={onViewDetail}
            disabled={!hasElement}
            type='button'
          >
            <Svg name='info' fill='primary/text' size='xs' />
            <Typography as='span' variant='p2/light' ml='xxs'>
              {t('viewDetail')}
            </Typography>
          </Button>
        )}
        {onRefreshData && (
          <Button
            data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-refresh`}
            variant='link/primary'
            onClick={onRefreshData}
            type='button'
          >
            <Svg name='refresh' fill='primary/text' size='xs' />
            <Typography as='span' variant='p2/light' ml='xxxs'>
              {t('refresh')}
            </Typography>
          </Button>
        )}
        {onFilter && (
          <Button
            data-testid={`${prefixTestId ? `${prefixTestId}-` : ''}button-on-filter`}
            ml='s'
            variant='link/primary'
            onClick={onFilter}
            type='button'
          >
            <Svg name='filter' fill='primary/text' size='xs' />
            <Typography as='span' variant='p2/light' ml='xxxs'>
              {t('filter')}
            </Typography>
          </Button>
        )}
      </Box>
    </Box>
  );
};
